import React, { useEffect, useState, useCallback } from 'react'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { BoldText } from './helpText'

function HelpImage({ assetId, alt, caption, resolveAssetUrl }) {
  const [src, setSrc] = useState(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    if (!assetId) return undefined
    let cancelled = false
    let objectUrl = null
    ;(async () => {
      try {
        const url = resolveAssetUrl(assetId)
        const r = await authFetch(url)
        if (!r.ok) throw new Error('load failed')
        const blob = await r.blob()
        objectUrl = URL.createObjectURL(blob)
        if (!cancelled) setSrc(objectUrl)
      } catch {
        if (!cancelled) setFailed(true)
      }
    })()
    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [assetId, resolveAssetUrl])

  if (!assetId) {
    return (
      <div className="flex items-center justify-center h-32 rounded-lg border-2 border-dashed border-slate-200 bg-slate-50 text-sm text-slate-400">
        No image
      </div>
    )
  }
  if (failed) {
    return <p className="text-xs text-slate-400 italic">Image unavailable</p>
  }
  if (!src) {
    return <div className="h-32 rounded-lg bg-slate-100 animate-pulse" />
  }
  return (
    <figure className="space-y-1 my-2">
      <img src={src} alt={alt || ''} className="rounded-md border border-slate-200 max-w-full h-auto" />
      {caption ? <figcaption className="text-[11px] text-slate-500 text-center">{caption}</figcaption> : null}
    </figure>
  )
}

const NOTE_STYLES = {
  info: 'border-sky-200 bg-sky-50 text-sky-900',
  warning: 'border-amber-200 bg-amber-50 text-amber-900',
}

export default function HelpBlockRenderer({
  blocks = [],
  resolveAssetUrl = (assetId) =>
    getLocalApiUrl(`/api/admin/help/assets/${encodeURIComponent(assetId)}`),
  className = '',
}) {
  if (!blocks?.length) return null

  return (
    <div className={`space-y-3 ${className}`}>
      {blocks.map((block, idx) => {
        if (!block || !block.type || block.type === 'legal_footer') return null
        switch (block.type) {
          case 'heading': {
            const level = Math.min(4, Math.max(2, Number(block.level) || 3))
            const Tag = `h${level}`
            const size =
              level === 2
                ? 'text-base font-bold text-slate-900'
                : level === 3
                  ? 'text-sm font-semibold text-slate-900'
                  : 'text-sm font-medium text-slate-800'
            return (
              <Tag key={idx} className={size}>
                {block.text || <span className="text-slate-300">Heading</span>}
              </Tag>
            )
          }
          case 'paragraph':
            return (
              <p key={idx} className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">
                {block.text ? <BoldText text={block.text} /> : <span className="text-slate-300">Start writing…</span>}
              </p>
            )
          case 'bullets':
            return (
              <ul key={idx} className="text-sm text-slate-600 leading-relaxed list-disc pl-5 space-y-1.5">
                {(block.items || []).filter(Boolean).map((item, i) => (
                  <li key={i}>
                    <BoldText text={item} />
                  </li>
                ))}
              </ul>
            )
          case 'note':
            return (
              <div
                key={idx}
                className={`rounded-md border px-3 py-2.5 text-sm leading-relaxed ${
                  NOTE_STYLES[block.variant] || NOTE_STYLES.info
                }`}
              >
                {block.text ? <BoldText text={block.text} /> : <span className="opacity-50">Callout text…</span>}
              </div>
            )
          case 'image':
            return (
              <HelpImage
                key={idx}
                assetId={block.asset_id}
                alt={block.alt}
                caption={block.caption}
                resolveAssetUrl={resolveAssetUrl}
              />
            )
          default:
            return null
        }
      })}
    </div>
  )
}
